import React from "react";
import { User, Building, Users } from "lucide-react";
import { MeetingParticipant } from "../../../types/meeting";
import UserParticipantCard from "./UserParticipantCard";
import DepartmentParticipantCard from "./DepartmentParticipantCard";

interface ParticipantListProps {
  participants: MeetingParticipant[];
  canUpdateMeeting: boolean;
  deleteLoading: string | null;
  onDeleteParticipant: (participant: MeetingParticipant) => void;
  buildDepartmentHierarchy: (participants: MeetingParticipant[]) => MeetingParticipant[];
}

export default function ParticipantList({
  participants,
  canUpdateMeeting,
  deleteLoading,
  onDeleteParticipant,
  buildDepartmentHierarchy,
}: ParticipantListProps) {
  if (participants.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <Users className="w-12 h-12 mx-auto text-gray-400 mb-3" />
        <p className="text-lg font-medium">Henüz Katılımcı Eklenmemiş</p>
        <p className="text-sm">Toplantınıza katılımcı eklemek için yukarıdaki butonu kullanın.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <User className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-blue-900">Kullanıcı Katılımcıları</h3>
            <p className="text-sm text-blue-700">{participants.filter((p) => p.type === "USER").length} kullanıcı</p>
          </div>
        </div>
        {participants.filter((p) => p.type === "USER").length === 0 ? (
          <div className="text-center py-6 text-blue-600 bg-blue-100 rounded-lg">
            <p className="text-sm">Henüz kullanıcı katılımcısı yok.</p>
          </div>
        ) : (
          <div className="border border-blue-200 rounded-lg overflow-hidden bg-white">
            <div className="max-h-80 overflow-y-auto divide-y bg-white">
              {participants
                .filter((p) => p.type === "USER")
                .map((participant) => (
                  <UserParticipantCard
                    key={participant.id}
                    participant={participant}
                    canDelete={canUpdateMeeting}
                    onDelete={onDeleteParticipant}
                    isDeleting={deleteLoading === participant.id}
                  />
                ))}
            </div>
          </div>
        )}
      </div>

      <div className="bg-green-50 rounded-xl p-6 border border-green-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <Building className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold text-green-900">Departman Katılımcıları</h3>
            <p className="text-sm text-green-700">{participants.filter((p) => p.type === "DEPARTMENT").length} departman</p>
          </div>
        </div>
        {participants.filter((p) => p.type === "DEPARTMENT").length === 0 ? (
          <div className="text-center py-6 text-green-600 bg-green-100 rounded-lg">
            <p className="text-sm">Henüz departman katılımcısı yok.</p>
          </div>
        ) : (
          <div className="border border-green-200 rounded-lg overflow-hidden bg-white">
            <div className="max-h-80 overflow-y-auto divide-y bg-white">
              {buildDepartmentHierarchy(participants.filter((p) => p.type === "DEPARTMENT")).map((participant) => (
                <DepartmentParticipantCard
                  key={participant.id}
                  participant={participant}
                  canDelete={canUpdateMeeting}
                  onDelete={onDeleteParticipant}
                  isDeleting={deleteLoading === participant.id}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
